import { RecordHistoryActionMenu } from '@/components/audit-log/RecordHistoryMenu'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { BillingBooleanSettingField } from '@/components/customers/BillingBooleanSettingField'
import SaveBar from '@/components/forms/SaveBar'
import { CashDrawerRegisterSettings } from '@/components/settings/CashDrawerRegisterSettings'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  cashDrawerQueries,
  cashDrawerQueryKeys,
  useCashDrawerLocations,
  useCashDrawerSettings,
  useCashDrawerWorkstations,
  useUpdateCashDrawerSettingsMutation,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import { cashDrawerSettingsSchema } from '@/lib/services/cash-drawers/cash-drawers.schema'
import { getErrorMessage } from '@/lib/utils'
import { isGlobalOrganization } from '@/lib/user-access'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, notFound, useParams } from '@tanstack/react-router'
import { Banknote, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/settings/cash-drawer/')({
  component: CashDrawerSettingsPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'cash-drawer-settings')) throw notFound()

    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )

    if (isGlobalOrganization(organization)) throw notFound()

    await Promise.all([
      context.queryClient.ensureQueryData(
        cashDrawerQueries.settings({ organizationId: params.orgId })
      ),
      context.queryClient.ensureQueryData(
        cashDrawerQueries.workstations({ organizationId: params.orgId })
      ),
      context.queryClient.ensureQueryData(
        cashDrawerQueries.locations({ organizationId: params.orgId })
      ),
    ])

    return { isReadOnly: !context.ability.can('edit', 'cash-drawer-settings') }
  },
})

export function CashDrawerSettingsPage() {
  const { orgId } = useParams({ strict: false })
  const path = usePathHelper()
  const { data } = useCashDrawerSettings({ organizationId: orgId })
  const { data: workstations = [] } = useCashDrawerWorkstations({ organizationId: orgId })
  const { data: locations = [] } = useCashDrawerLocations({ organizationId: orgId })

  return (
    <div className="max-w-4xl space-y-5">
      <AdminPageHeader
        icon={Banknote}
        title="Cash Drawer"
        description="Set up the registers your team uses to count and track cash and checks."
        actions={
          <RecordHistoryActionMenu
            link={path.cashDrawerSettings.link('history')}
            triggerLabel="More cash drawer actions"
          />
        }
      />
      <CashDrawerSettingsForm key={String(data?.version)} organizationId={orgId} settings={data} />
      {data?.enabled ? (
        <CashDrawerRegisterSettings workstations={workstations} locations={locations} />
      ) : null}
    </div>
  )
}

function CashDrawerSettingsForm({ organizationId, settings }) {
  const { isReadOnly } = useIsReadOnly()
  const queryClient = useQueryClient()
  const updateMutation = useUpdateCashDrawerSettingsMutation()
  const form = useAppForm({
    defaultValues: getCashDrawerSettingsFormDefaults(settings),
    validators: {
      onSubmit: cashDrawerSettingsSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return

        try {
          const response = await updateMutation.mutateAsync(value)
          formApi.reset(getCashDrawerSettingsFormDefaults(response))
          toast.success(
            response?.enabled ? 'Cash drawer tracking is on' : 'Cash drawer tracking is off'
          )
        } catch (error) {
          const response = error?.response?.data

          if (
            error?.response?.status === 409 &&
            response?.type === 'organization_cash_drawer_settings_version_conflict' &&
            response.current
          ) {
            queryClient.setQueryData(
              cashDrawerQueryKeys.settings({ organizationId }),
              response.current
            )
            formApi.reset(getCashDrawerSettingsFormDefaults(response.current))
            toast.error('These cash drawer settings changed in another session', {
              description: 'We loaded the latest saved value. Review it before saving again.',
            })
            return
          }

          if (error?.response?.status === 409 && response?.type === 'cash_drawer_open_sessions') {
            queryClient.setQueryData(cashDrawerQueryKeys.settings({ organizationId }), (current) =>
              getCashDrawerSettingsFromOpenSessionsConflict(current, response)
            )
            formApi.reset(getCashDrawerSettingsFormDefaults(response))
          }

          toast.error('Cash drawer settings were not changed', {
            description: getErrorMessage(error),
          })
        }
      },
    },
  })

  return (
    <div>
      <form.AppForm>
        <form.Form>
          <Card>
            <CardHeader>
              <CardTitle>Cash drawer tracking</CardTitle>
              <CardDescription>
                When this is on, staff must start a cash drawer before recording cash or check
                payments and refunds.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <form.AppField name="enabled">
                {(field) => (
                  <BillingBooleanSettingField
                    field={field}
                    icon={ShieldCheck}
                    title="Track cash drawers"
                    description="Show cash drawer controls in POS and require a started cash drawer for manual payment activity."
                    disabled={isReadOnly}
                  />
                )}
              </form.AppField>

              <Typography variant="supporting" className="block rounded-lg border bg-muted/20 p-4">
                Turning tracking off requires every cash drawer to be counted first. Previous drawer
                sessions and reports remain available.
                {settings?.open_session_count
                  ? ` ${settings.open_session_count} cash drawer${settings.open_session_count === 1 ? ' is' : 's are'} currently in use.`
                  : ''}
              </Typography>
            </CardContent>
          </Card>

          {!isReadOnly ? <SaveBar mutation={updateMutation} /> : null}
        </form.Form>
      </form.AppForm>
    </div>
  )
}

export function getCashDrawerSettingsFormDefaults(settings = {}) {
  return {
    expected_version: settings.version,
    enabled: Boolean(settings.enabled),
  }
}

export function getCashDrawerSettingsFromOpenSessionsConflict(current = {}, response = {}) {
  return {
    ...current,
    organization_id: response.organization_id,
    version: response.version,
    enabled: response.enabled,
    updated_by: response.updated_by,
    updated_at: response.updated_at,
    open_session_count: response.open_session_count,
    can_disable: response.can_disable,
  }
}
